'use client';

import Grid from '@/components/_rebrand/Grid';
import LottieMedia from '@/components/_rebrand/LottieMedia';
import { LottieAnimation } from '@/components/_rebrand/LottieMedia/LottieMedia.types';
import { Text } from '@/components/_rebrand/Text';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { FC } from 'react';

export type AnimatedTitleHeroTitleImage = {
  src: string;
  alt: string;
};

export type AnimatedTitleHeroAnimation = 'slide-in' | 'static';

export type AnimatedTitleHeroProps = {
  eyebrow?: string;
  titleSVG: AnimatedTitleHeroTitleImage;
  /**
   * Optional Lottie JSON animation for the title, as a fetchable URL or an
   * already-parsed animation object. When provided, the Lottie animation is
   * rendered in place of the animated SVG, using `titleSVG.src` as the
   * fallback image (e.g. for reduced motion or while loading). When omitted,
   * the animated SVG is used as before.
   */
  titleAnimation?: LottieAnimation;
  /**
   * Controls how the title SVG animates in. `'slide-in'` reveals the title with
   * a clip-path wipe; `'static'` renders it with no animation. Defaults to
   * `'static'`.
   */
  animationType?: AnimatedTitleHeroAnimation;
  description?: string | React.ReactNode;
};

const TITLE_SVG_WIDTH = 922;
const TITLE_SVG_HEIGHT = 175;

const AnimatedTitleHero: FC<AnimatedTitleHeroProps> = ({
  eyebrow,
  titleSVG,
  titleAnimation,
  animationType = 'static',
  description,
}) => (
  <section className="bg-gray-950 text-white">
    <Grid className="py-margin-section-md">
      <div className="col-span-4 md:col-span-8 md:col-start-3 flex flex-col items-center gap-y-s text-center">
        {eyebrow && (
          <Text type="micro1" as="span" className="text-violet-200">
            {eyebrow}
          </Text>
        )}
        <h1 className="w-full">
          {titleAnimation ? (
            <LottieMedia
              animation={titleAnimation}
              fallbackSrc={titleSVG.src}
              alt={titleSVG.alt}
              className="max-w-[922px] mx-auto"
            />
          ) : (
            <motion.span
              className="block w-full max-w-[922px] mx-auto"
              initial={animationType === 'slide-in' ? { clipPath: 'inset(0 100% 0 0)' } : undefined}
              animate={animationType === 'slide-in' ? { clipPath: 'inset(0 0% 0 0)' } : undefined}
              transition={
                animationType === 'slide-in'
                  ? { duration: 2, ease: [0.65, 0, 0.35, 1], delay: 0.2 }
                  : undefined
              }
            >
              <Image
                src={titleSVG.src}
                alt={titleSVG.alt}
                width={TITLE_SVG_WIDTH}
                height={TITLE_SVG_HEIGHT}
                priority
                unoptimized
                className="w-full h-auto"
              />
            </motion.span>
          )}
        </h1>
        {description && (
          <Text type="p1" className="text-violet-200">
            {description}
          </Text>
        )}
      </div>
    </Grid>
  </section>
);

export default AnimatedTitleHero;
